import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';

import { Input, Select } from '@treams/treams-ui';
import BulkModal from './BulkModal';
import { LocalDate, nativeJs } from '@js-joda/core';

const TableCell = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
`;

const TableHeader = styled(TableCell)`
	font-weight: bold;
	font-size: 12px;
`;

function CyclePreviewMoments(props: { cycleTemplateID: string; startDate: Date }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const { data, loading } = graphql.useCycleTemplateQuery({
		variables: {
			id: props.cycleTemplateID,
		},
	});

	if (loading) {
		return (
			<Flex justifyContent="center" flexGrow={1}>
				<Spinner isLoading size="24px" />
			</Flex>
		);
	}

	return (
		<>
			<Box mt={3}>
				<Flex mb={3} alignItems="center">
					<TableHeader flexGrow={1}>{t('employees.bulkAssignCycle.preview.moment')}</TableHeader>
					<TableHeader width={100}>{t('employees.bulkAssignCycle.preview.startDate')}</TableHeader>
					<TableHeader width={100}>{t('employees.bulkAssignCycle.preview.endDate')}</TableHeader>
				</Flex>
				<HorizontalRule />
			</Box>
			{data?.cycleTemplate.moments.map((momentTemplate, index) => (
				<Box key={index}>
					<Flex alignItems="center" paddingY={10}>
						<Flex flex={1} alignItems="center">
							<TableCell marginRight={12}>{localize(momentTemplate.title).text}</TableCell>
						</Flex>
						<TableCell width={100}>
							{moment(props.startDate).add(momentTemplate.startOffset, 'days').format('DD-MM-YYYY')}
						</TableCell>
						<TableCell width={100}>
							{moment(props.startDate)
								.add(momentTemplate.startOffset + momentTemplate.duration, 'days')
								.format('DD-MM-YYYY')}
						</TableCell>
					</Flex>
					<HorizontalRule />
				</Box>
			))}
		</>
	);
}

export default function BulkAssignCycleModal(props: {
	users: graphql.User[];
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const cycles = graphql.useCycleTemplatesQuery({
		variables: {
			isArchived: false,
			first: 100,
		},
	});

	const [createCycleInstance] = graphql.useCreateCycleInstanceMutation();

	const options = cycles.data?.cycleTemplates.edges.map(({ node }) => ({
		title: localize(node.title).text,
		value: node.id,
	}));
	options?.sort((a, b) => a.title.localeCompare(b.title));

	const [cycleTemplateID, setCycleTemplateID] = useState<string | undefined>();
	const [startDate, setStartDate] = useState(new Date());

	return (
		<BulkModal
			valid={!!cycleTemplateID}
			name="bulkAssignCycle"
			onDismiss={props.onDismiss}
			users={props.users}
			apply={async (user) => {
				const localStartDate = LocalDate.from(nativeJs(startDate));
				await createCycleInstance({
					variables: {
						input: {
							localStartDate,
							activatesAt: localStartDate,
							userID: user.id,
							cycleTemplateID: cycleTemplateID!,
						},
					},
				});
			}}
		>
			<Flex mb={3}>
				<Box mr={2} flexGrow={1} flexShrink={1} flexBasis={0}>
					<Label title={t('employees.bulkAssignCycle.cycle.label')} />
					{cycles.data ? (
						<Select
							placeholder={t('ui.select.placeholder')}
							options={options}
							selectedValue={cycleTemplateID}
							onChange={(value) => setCycleTemplateID(value)}
						/>
					) : (
						<Spinner isLoading size="18px" />
					)}
				</Box>

				<Box ml={2} flexGrow={1} flexShrink={1} flexBasis={0}>
					<Label title={t('employees.bulkAssignCycle.startDate.label')} />
					<Input.DatePicker
						value={startDate}
						onChange={(startDate) => setStartDate(startDate ?? new Date())}
					/>
				</Box>
			</Flex>

			{cycleTemplateID && (
				<Box my={3}>
					<HorizontalRule />
					<CyclePreviewMoments startDate={startDate} cycleTemplateID={cycleTemplateID} />
				</Box>
			)}
		</BulkModal>
	);
}
